import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { X, ChevronLeft, ChevronRight, Share2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from './ui/button';

const LightboxModal = ({ image, images, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (image && images) {
      const index = images.findIndex(img => img === image);
      setCurrentIndex(index !== -1 ? index : 0);
    }
  }, [image, images]);

  const goToNext = React.useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  const goToPrevious = React.useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setIsZoomed(false);
  }, [images.length]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Wedding Photo',
        text: 'Check out this beautiful wedding photo!',
        url: images[currentIndex]
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(images[currentIndex]);
      alert('Image link copied to clipboard!');
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, goToNext, goToPrevious, onClose]);

  if (!images || images.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        overlayClassName="bg-transparent"
        className="max-w-6xl h-[90vh] overflow-hidden rounded-3xl border border-white/30 bg-white/10 p-0 shadow-2xl backdrop-blur-md"
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/35 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div className="text-white text-sm drop-shadow-sm">
              {currentIndex + 1} / {images.length}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsZoomed(!isZoomed)}
                className="text-white hover:bg-white/20"
              >
                {isZoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="text-white hover:bg-white/20"
              >
                <Share2 className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Image Container */}
        <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-transparent p-6 pt-20 pb-24">
          <img
            src={images[currentIndex]}
            alt={`Photo ${currentIndex + 1}`}
            className={`max-h-full max-w-full object-contain transition-transform duration-300 ${
              isZoomed ? 'scale-110 cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            decoding="async"
            onClick={() => setIsZoomed(!isZoomed)}
          />
        </div>

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <Button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 z-20 h-12 w-12 -translate-y-1/2 rounded-full bg-white/15 p-0 text-white backdrop-blur-md hover:bg-white/25"
              variant="ghost"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              onClick={goToNext}
              className="absolute right-4 top-1/2 z-20 h-12 w-12 -translate-y-1/2 rounded-full bg-white/15 p-0 text-white backdrop-blur-md hover:bg-white/25"
              variant="ghost"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </>
        )}

        {/* Thumbnail Strip */}
        <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/35 to-transparent p-4">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {images.slice(0, 10).map((img, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? 'ring-2 ring-white scale-110'
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
            {images.length > 10 && (
              <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center text-white text-xs">
                +{images.length - 10}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LightboxModal;
